import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { PENDING_ORG_INVITE_TOKEN_KEY } from '../constants/orgInvite'
import { saveContinuation, loadContinuation, boundedContext, appendContinuation } from '../context/oauthContinuation'

/**
 * Signup, as pages/SignupPage.jsx does it, for the customer build's Control Board-styled signup page: the same fields
 * and password rules, the same signup call, the invitation token and the bounded MCP authorization continuation carried
 * exactly as there, and the same next stop (/verify-email). Keep the two in step.
 */
export function useSignupFlow() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { signup } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const t = searchParams.get('token')?.trim()
    if (t) {
      try {
        localStorage.setItem(PENDING_ORG_INVITE_TOKEN_KEY, t)
      } catch {
        /* ignore */
      }
    }
  }, [searchParams])

  // A pending MCP OAuth continuation on the /signup URL survives signup -> verify -> invitation (as SignupPage).
  useEffect(() => {
    saveContinuation(typeof window !== 'undefined' ? window.location.search : '')
  }, [])

  const passwordChecks = (password) => [
    { label: 'At least 8 characters', check: password.length >= 8 },
    { label: 'Contains a number', check: /\d/.test(password) },
    { label: 'Contains uppercase letter', check: /[A-Z]/.test(password) },
  ]

  const submit = async ({ name, email, password, companyName, jobTitle, signupRoleBand }) => {
    setError('')
    if (!passwordChecks(password).every((c) => c.check)) {
      setError('Please meet all password requirements')
      return
    }
    if (!companyName.trim() || !jobTitle.trim() || !signupRoleBand) {
      setError('Please add your company, title, and role.')
      return
    }
    setLoading(true)
    try {
      await signup(email, password, name, {
        company_name: companyName.trim(),
        job_title: jobTitle.trim(),
        signup_role_band: signupRoleBand,
      })
      const fromUrl = boundedContext(typeof window !== 'undefined' ? window.location.search : '')
      const pendingOAuthContext = fromUrl || loadContinuation()
      const dest = pendingOAuthContext && fromUrl ? appendContinuation('/verify-email', pendingOAuthContext) : '/verify-email'
      navigate(dest, { replace: true })
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return { submit, loading, error, passwordChecks }
}
